import {
  AuthDesktopCard,
  AuthDesktopHeader,
  AuthDesktopLayout,
  AuthScreenContent,
  AuthScreenHeader,
  AuthScreenLayout,
  AuthScreenSpacer,
  authScreenStyles,
} from "@/components/auth/auth-screen-layout";
import { ThemedText } from "@/components/themed-text";
import { BrandButton } from "@/components/ui/brand-button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BrandColors, Colors } from "@/constants/theme";
import { useDesktopLayout } from "@/hooks/use-desktop-layout";
import authService from "@/lib/auth";
import { showToast } from "@/lib/toast";
import {
  ResetPasswordFormData,
  resetPasswordSchema,
} from "@/lib/validation/auth";
import { zodResolver } from "@hookform/resolvers/zod";
import Ionicons from "@react-native-vector-icons/ionicons";
import { useLocalSearchParams, useRouter } from "expo-router";
import React, { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  Modal,
  StyleSheet,
  TouchableOpacity,
  View,
  useColorScheme,
} from "react-native";
import { HelperText, TextInput as PaperTextInput } from "react-native-paper";

const EXPIRED_TOKEN_MESSAGE =
  "Unable to reset password. The token may be invalid or expired.";

export default function ResetPasswordScreen() {
  const params = useLocalSearchParams<{ email: string; token: string }>();
  const router = useRouter();
  const colorScheme = useColorScheme();
  const theme = Colors[colorScheme ?? "light"];
  const desktop = useDesktopLayout();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [expiredTokenVisible, setExpiredTokenVisible] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      token: params.token,
      email: params.email || "",
      password: "",
      password_confirmation: "",
    },
  });

  const onSubmit = async (data: ResetPasswordFormData) => {
    try {
      await authService.resetPassword({
        email: data.email,
        token: data.token,
        password: data.password,
        password_confirmation: data.password_confirmation,
      });
      showToast.success("Password reset successfully");
      setTimeout(() => {
        router.replace("/(auth)");
      }, 1500);
      return true;
    } catch (error: any) {
      console.log(error.response?.data);
      const message =
        typeof error.response?.data?.message === "string"
          ? error.response.data.message
          : "Failed to reset password";

      if (message.includes(EXPIRED_TOKEN_MESSAGE)) {
        setExpiredTokenVisible(true);
        return;
      }

      showToast.error(message);
    }
  };

  const handleRequestResetAgain = () => {
    setExpiredTokenVisible(false);
    router.replace("/(auth)/forgot-password");
  };

  const handleBack = () => {
    if (router.canGoBack()) {
      router.back();
    } else {
      router.replace("/(auth)");
    }
  };

  const title = "Reset Password";
  const subtitle = "Please enter your new password below";

  const inputStyle = [
    authScreenStyles.input,
    desktop && authScreenStyles.desktopInput,
  ];
  const inputOutlineStyle = desktop
    ? authScreenStyles.desktopInputOutline
    : authScreenStyles.inputOutline;

  const formFields = (
    <View
      style={[
        desktop ? authScreenStyles.desktopForm : authScreenStyles.form,
        desktop && styles.desktopForm,
      ]}
    >
      <View style={authScreenStyles.field}>
        <Label label={desktop ? "Email address" : "Email"} />
        <Controller
          control={control}
          name="email"
          render={({ field: { onChange, onBlur, value } }) => (
            <Input
              placeholder={desktop ? "you@example.com" : "Enter your email"}
              value={value}
              onChangeText={onChange}
              onBlur={onBlur}
              keyboardType="email-address"
              autoCapitalize="none"
              autoComplete="email"
              error={!!errors.email}
              style={inputStyle}
              outlineStyle={inputOutlineStyle}
            />
          )}
        />
        {errors.email && (
          <HelperText type="error" visible={!!errors.email}>
            {errors.email?.message}
          </HelperText>
        )}
      </View>

      <View style={authScreenStyles.field}>
        <Label label="New Password" />
        <Controller
          control={control}
          name="password"
          render={({ field: { onChange, onBlur, value } }) => (
            <Input
              placeholder="Enter new password"
              value={value}
              onChangeText={onChange}
              onBlur={onBlur}
              secureTextEntry={!showPassword}
              error={!!errors.password}
              style={inputStyle}
              outlineStyle={inputOutlineStyle}
              right={
                <PaperTextInput.Icon
                  icon={showPassword ? "eye-off" : "eye"}
                  size={desktop ? 18 : undefined}
                  color={desktop ? BrandColors.mediumGray : undefined}
                  onPress={() => setShowPassword(!showPassword)}
                />
              }
              testID="New Password"
            />
          )}
        />
        {errors.password && (
          <HelperText type="error" visible={!!errors.password}>
            {errors.password?.message}
          </HelperText>
        )}
      </View>

      <View style={authScreenStyles.field}>
        <Label label="Confirm Password" />
        <Controller
          control={control}
          name="password_confirmation"
          render={({ field: { onChange, onBlur, value } }) => (
            <Input
              placeholder="Confirm new password"
              value={value}
              onChangeText={onChange}
              onBlur={onBlur}
              secureTextEntry={!showConfirmPassword}
              error={!!errors.password_confirmation}
              style={inputStyle}
              outlineStyle={inputOutlineStyle}
              right={
                <PaperTextInput.Icon
                  icon={showConfirmPassword ? "eye-off" : "eye"}
                  size={desktop ? 18 : undefined}
                  color={desktop ? BrandColors.mediumGray : undefined}
                  onPress={() => setShowConfirmPassword(!showConfirmPassword)}
                />
              }
              testID="Confirm Password"
            />
          )}
        />
        {errors.password_confirmation && (
          <HelperText type="error" visible={!!errors.password_confirmation}>
            {errors.password_confirmation?.message}
          </HelperText>
        )}
      </View>
    </View>
  );

  const submitButton = (
    <BrandButton
      variant="primary"
      loading={isSubmitting}
      onPress={handleSubmit(onSubmit)}
      style={
        desktop
          ? authScreenStyles.desktopContinueButton
          : authScreenStyles.continueButton
      }
      labelStyle={
        desktop
          ? authScreenStyles.desktopContinueButtonLabel
          : authScreenStyles.continueButtonLabel
      }
      contentStyle={authScreenStyles.continueButtonContent}
      icon={() => (
        <Ionicons
          name="chevron-forward"
          size={desktop ? 14 : 18}
          color={BrandColors.white}
        />
      )}
    >
      {isSubmitting ? "Resetting..." : "Reset Password"}
    </BrandButton>
  );

  const expiredTokenModal = (
    <Modal
      visible={expiredTokenVisible}
      transparent
      animationType="fade"
      onRequestClose={() => setExpiredTokenVisible(false)}
    >
      <View style={styles.modalOverlay}>
        <View
          style={[styles.modalContent, { backgroundColor: theme.background }]}
        >
          <ThemedText style={styles.modalTitle} type="defaultSemiBold">
            Reset link expired
          </ThemedText>
          <ThemedText style={styles.modalMessage}>
            This reset link is invalid or has expired. Request a password reset
            again to get a new link.
          </ThemedText>
          <View style={styles.modalButtons}>
            <TouchableOpacity
              style={[
                styles.modalButton,
                styles.cancelButton,
                { borderColor: theme.border },
              ]}
              onPress={() => setExpiredTokenVisible(false)}
              accessibilityRole="button"
              accessibilityLabel="Stay on this screen"
            >
              <ThemedText type="defaultSemiBold">Cancel</ThemedText>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.modalButton, styles.confirmButton]}
              onPress={handleRequestResetAgain}
              accessibilityRole="button"
              accessibilityLabel="Request password reset again"
            >
              <ThemedText
                style={styles.confirmButtonText}
                type="defaultSemiBold"
              >
                Request again
              </ThemedText>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );

  if (desktop) {
    return (
      <AuthDesktopLayout onBack={handleBack}>
        <AuthDesktopHeader title={title} subtitle={subtitle} />
        <AuthDesktopCard>
          {formFields}
          {submitButton}
        </AuthDesktopCard>
        {expiredTokenModal}
      </AuthDesktopLayout>
    );
  }

  return (
    <AuthScreenLayout onBack={handleBack} keyboardInset>
      <AuthScreenContent>
        <AuthScreenHeader title={title} subtitle={subtitle} />

        {formFields}
      </AuthScreenContent>

      <AuthScreenSpacer />

      <AuthScreenContent>{submitButton}</AuthScreenContent>

      {expiredTokenModal}
    </AuthScreenLayout>
  );
}

const styles = StyleSheet.create({
  desktopForm: {
    marginBottom: 20,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    justifyContent: "center",
    alignItems: "center",
    padding: 24,
  },
  modalContent: {
    width: "100%",
    maxWidth: 340,
    borderRadius: 16,
    padding: 24,
    gap: 16,
  },
  modalTitle: {
    fontSize: 18,
    textAlign: "center",
  },
  modalMessage: {
    textAlign: "center",
    lineHeight: 22,
  },
  modalButtons: {
    flexDirection: "row",
    gap: 12,
  },
  modalButton: {
    flex: 1,
    minHeight: 48,
    borderRadius: 12,
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 8,
  },
  cancelButton: {
    borderWidth: 1,
  },
  confirmButton: {
    backgroundColor: BrandColors.primary,
  },
  confirmButtonText: {
    color: BrandColors.white,
    textAlign: "center",
  },
});
